import { useEffect, useRef, useState } from "react";
import { Box, ClickAwayListener, Popper, TextField } from "@mui/material";

// Per COMY1-279 review feedback: the picker offers full and half hours as a
// single list (00:00, 00:30, 01:00 … 23:30) rather than separate hours/minutes
// columns.
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

const baseStyles = {
  width: "92px",
  "& .MuiInputBase-root": {
    height: "39px",
    borderRadius: "8px",
    fontSize: "14px",
  },
  "& input": {
    textAlign: "center",
    padding: "8px 10px",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(37, 124, 109, 1)",
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(37, 124, 109, 0.7)",
  },
};

const popoverStyles = {
  zIndex: 1300,
  bgcolor: "#fff",
  border: "1px solid rgba(165, 165, 165, 0.4)",
  borderRadius: "8px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  mt: "4px",
  overflow: "hidden",
};

const columnStyles = {
  width: "92px",
  maxHeight: "200px",
  overflowY: "auto",
  py: "4px",
};

const itemStyles = (selected) => ({
  px: "10px",
  py: "6px",
  textAlign: "center",
  fontSize: "14px",
  cursor: "pointer",
  backgroundColor: selected ? "rgba(37, 124, 109, 0.12)" : "transparent",
  color: selected ? "rgba(37, 124, 109, 1)" : "rgba(47, 43, 61, 0.9)",
  fontWeight: selected ? 500 : 400,
  "&:hover": {
    backgroundColor: selected
      ? "rgba(37, 124, 109, 0.18)"
      : "rgba(37, 124, 109, 0.06)",
  },
});

function normalizeOnBlur(value) {
  if (!value) return null;
  const digits = value.replace(/\D/g, "");
  if (digits.length === 0) return null;
  const padded = digits.padEnd(4, "0").slice(0, 4);
  const hh = Math.min(23, parseInt(padded.slice(0, 2), 10) || 0);
  const mm = Math.min(59, parseInt(padded.slice(2, 4), 10) || 0);
  // Snap to the nearest half hour (:00 or :30) so typed values stay on the
  // same grid as the picker. Cap at 23:30 so a round-up never rolls to 24:00.
  const snapped = Math.min(Math.round((hh * 60 + mm) / 30) * 30, 23 * 60 + 30);
  const h = Math.floor(snapped / 60);
  const m = snapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Masked HH:MM time input with a click-to-open picker popover.
 *
 * Two ways to set a time:
 *   1. Type digits — colon auto-inserts after the hour pair; blur snaps to
 *      the nearest valid half hour (HH 00-23, MM 00 or 30).
 *   2. Click an entry in the half-hour list in the popover.
 *
 * Avoids `<input type=time>` so styling stays in our control.
 *
 * @param {string|null} value
 * @param {(next: string|null) => void} onChange
 * @param {boolean} [disabled]
 * @param {string} [placeholder]
 */
const TimeInput = ({ value, onChange, onBlur, disabled, placeholder = "00:00", error = false }) => {
  const anchorRef = useRef(null);
  const popperContentRef = useRef(null);
  const selectedItemRef = useRef(null);
  const [open, setOpen] = useState(false);
  // Tracks whether the user actually changed the value during the current
  // focus/picker session. A click-in followed by click-out without typing
  // anything shouldn't surface an error for this field.
  const valueChangedRef = useRef(false);

  const openPicker = () => {
    if (disabled) return;
    valueChangedRef.current = false;
    setOpen(true);
  };

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
    valueChangedRef.current = true;
    if (digits.length === 0) {
      onChange("");
      return;
    }
    const formatted = digits.length > 2
      ? `${digits.slice(0, 2)}:${digits.slice(2)}`
      : digits;
    onChange(formatted);
  };

  const handleBlur = () => {
    onChange(normalizeOnBlur(value));
    if (valueChangedRef.current) onBlur?.();
  };

  const errorBorderSx = error
    ? {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#d32f2f",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#d32f2f",
        },
      }
    : {};

  // Picking an entry sets the whole time at once (unlike the old two-column
  // picker, one click is a complete selection), so close the picker and treat
  // it as a committed change.
  const handleSelectTime = (time) => {
    valueChangedRef.current = true;
    onChange(time);
    setOpen(false);
    onBlur?.();
  };

  // Bring the current value into view when the list opens so the user doesn't
  // land at 00:00 and scroll down to a late time.
  useEffect(() => {
    if (open && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: "center" });
    }
  }, [open]);

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        // The Popper is portaled, so clicks on its items would normally count
        // as "outside" and close the picker after the first selection. Skip
        // dismissal when the click target is inside the popper content.
        if (
          popperContentRef.current &&
          event.target instanceof Node &&
          popperContentRef.current.contains(event.target)
        ) {
          return;
        }
        // Only treat as a dismissal (and mark touched) when the picker was
        // actually open AND the user actually changed the value during this
        // session. Otherwise unrelated outside clicks — checking another
        // day's checkbox, or briefly opening this picker and clicking out —
        // would mark the field touched and surface errors the user hasn't
        // earned yet.
        if (open) {
          setOpen(false);
          if (valueChangedRef.current) onBlur?.();
        }
      }}
    >
      <Box sx={{ position: "relative", display: "inline-block" }} ref={anchorRef}>
        <TextField
          value={value || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={openPicker}
          onClick={openPicker}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          inputProps={{
            maxLength: 5,
            inputMode: "numeric",
            autoComplete: "off",
            "data-form-type": "other",
            dir: "ltr",
          }}
          sx={{ ...baseStyles, ...errorBorderSx }}
        />
        <Popper
          open={open && !disabled}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        >
          {/* dir="ltr" pins the picker to LTR so the time list stays LTR even
              on RTL pages — time format is always LTR. */}
          <Box ref={popperContentRef} dir="ltr" sx={{ display: "flex", ...popoverStyles }}>
            <Box sx={columnStyles}>
              {TIME_OPTIONS.map((time) => {
                const selected = time === value;
                return (
                  <Box
                    key={time}
                    ref={selected ? selectedItemRef : undefined}
                    sx={itemStyles(selected)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelectTime(time)}
                  >
                    {time}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default TimeInput;
